import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import { InfoCircleIcon, PlusIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { useFinancialAccounts } from '../../../hooks/queries/useFinancialAccounts';
import { useHouseholdNetWorth } from '../../../hooks/queries/useHouseholdNetWorth';
import { useFeatureGates } from '../../../hooks/useFeatureGates';
import { PATHS } from '../../../paths';
import { useNavigateWithDateRange } from '../../../providers/DateRangeProvider';
import { isSupportedFinancialAccount } from '../../../utils/account/accounts';
import { ACCOUNT_SECTIONS, groupAccountsBySection } from '../../../utils/account/accountSections';
import { netWorthTotalLabel, netWorthTotalText } from '../../../utils/account/netWorth';
import AccountGroup from '../../AccountGroup';
import PlaidLinkButton from '../../plaid/PlaidLinkButton';
import Widget from '../../Widget';

const HEADER_CLASS_NAME = 'border-secondary bg-transparent py-3 pl-5 pr-3';

const TOTAL_TOOLTIP =
  'Additional debts (e.g. loans and mortgages) and additional assets (e.g. property) are not yet included.';

/**
 * Right-column widget on the dashboard showing the household's net worth over its
 * financial accounts grouped by section (checking, savings, credit cards, and investments
 * behind the investments gate). Includes a "connect" button via Plaid Link and navigates
 * to the account detail pane on click.
 *
 * The total is the server's `householdNetWorth`, never a client sum. The sections add up
 * to it: they leave out the accounts it excludes, and list investment accounts on the same
 * gate the server counts them on. A failed net-worth read, or no gate set read at all,
 * shows an em dash over the sections, which still render. A row opens its account route;
 * an investment account's is the investment pane. Sync status is per account row.
 */
const DashboardAccountsWidget = () => {
  const navigate = useNavigateWithDateRange();
  const { financialAccounts, loading } = useFinancialAccounts();
  const { netWorth, excludedAccounts, loading: netWorthLoading } = useHouseholdNetWorth();
  const { flags, loading: gatesLoading, resolved: gatesResolved } = useFeatureGates();
  const includeInvestments = flags.has(FeatureGate.InvestmentAccounts);

  const filteredAccounts = React.useMemo(
    () => financialAccounts?.filter((account) => isSupportedFinancialAccount(account, { includeInvestments })) ?? [],
    [financialAccounts, includeInvestments]
  );

  const groupedAccounts = React.useMemo(
    () => groupAccountsBySection(filteredAccounts, { includeInvestments }),
    [filteredAccounts, includeInvestments]
  );

  const handleAccountClick = (accountId: string) => navigate(PATHS.ACCOUNT(accountId));

  const addButton = (
    <PlaidLinkButton
      aria-label="Add account"
      color="tertiary"
      size="sm"
      iconLeading={<PlusIcon className="size-6" />}
    />
  );

  // The first gate read holds the widget loading, so the investments section never appears late.
  if (loading || (netWorthLoading && !netWorth) || (gatesLoading && !gatesResolved)) {
    return (
      <Widget>
        <Widget.Header title="Accounts" className={HEADER_CLASS_NAME} />
        <Widget.Body>
          <LoadingIndicator className="h-[200px]" />
        </Widget.Body>
      </Widget>
    );
  }

  if (filteredAccounts.length === 0) {
    return null;
  }

  // With no gate set read, the sections are gate-off while the server may still count investment
  // accounts, so the total is withheld rather than shown over sections it may not match.
  const shownNetWorth = gatesResolved ? netWorth : undefined;

  const nonEmptySections = ACCOUNT_SECTIONS.filter((section) => groupedAccounts[section].length > 0);

  return (
    <Widget>
      <Widget.Header title="Accounts" trailing={addButton} className={HEADER_CLASS_NAME} />
      <div className="bg-tertiary border-secondary flex items-center justify-between gap-2 border-b py-3 pl-5 pr-4">
        <span className="text-md text-primary font-semibold">
          {netWorthTotalLabel(shownNetWorth?.asOf, new Date())}
        </span>
        <div className="flex shrink-0 items-center gap-0.5">
          <span className="text-primary text-xl font-semibold">{netWorthTotalText(shownNetWorth)}</span>
          <Tooltip title={TOTAL_TOOLTIP}>
            <TooltipTrigger
              aria-label="What the total includes"
              className="text-fg-quaternary flex w-7 justify-center py-0.5"
            >
              <InfoCircleIcon className="size-4 shrink-0" />
            </TooltipTrigger>
          </Tooltip>
        </div>
      </div>
      {/* `pb-2` keeps a collapsed last group's bottom border off the card's own border. */}
      <Widget.Body className="bg-primary pb-2">
        {nonEmptySections.map((section, index) => (
          <AccountGroup
            key={section}
            section={section}
            accounts={groupedAccounts[section]}
            excludedAccounts={excludedAccounts}
            onAccountClick={handleAccountClick}
            compact
            flat
            showTopBorder={index > 0}
          />
        ))}
      </Widget.Body>
    </Widget>
  );
};

export default DashboardAccountsWidget;
